import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Path3Splunk from './Path3/Path3Splunk';
import Path3Elastic from './Path3/Path3Elastic';
import Path3Chronicle from './Path3/Path3Chronicle';
import Path3ArcSight from './Path3/Path3ArcSight';
import LiveEventPath3Splunk from './Path3/LiveEventPath3Splunk';
import LiveEventPath3Elastic from './Path3/LiveEventPath3Elastic';
import LiveEventPath3Chronicle from './Path3/LiveEventPath3Chronicle';
import LiveEventPath3ArcSight from './Path3/LiveEventPath3ArcSight';

// Architecture diagram sources/destinations (from ConfluentArchitecture)
const sources = [
  { icon: "🔥", label: "Firewalls", top: 80 },
  { icon: "🛡️", label: "EDR/XDR", top: 190 },
  { icon: "☁️", label: "Cloud", top: 300 },
  { icon: "📊", label: "SIEM Agents", top: 410 },
  { icon: "💻", label: "Applications", top: 520 },
  { icon: "🌐", label: "Network", top: 630 },
  { icon: "🔐", label: "Identity", top: 740 },
  { icon: "📧", label: "Email", top: 850 },
];
const destinations = [
  { logo: "https://cdn.ivaluetv.com/oem/splunk.svg", label: "Splunk", top: 150, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/elastic.png", label: "Elastic", top: 260, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/OpenText_logo.svg", label: "ArcSight", top: 370, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/Chronicle-RGB-Horz-Color.webp", label: "Google Chronicle", top: 480, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/public/icons8-data-lake-64.png", label: "Data Lake", top: 620, color: "#8b5cf6" },
];

// Dummy metrics
const metrics = [
  { label: 'Storage Size', value: '12.4 TB', color: '#00b4a0' },
  { label: 'No. of Events', value: '8,234,120', color: '#1e90ff' },
  { label: 'Total Topics', value: 42, color: '#ab47bc' },
  { label: 'Total Consumption', value: '6.2 TB', color: '#ffa726' },
];



function ArchitecturePath3({ activeTab }) {
  const diagramRef = useRef();
  const svgRef = useRef();
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const wrapper = diagramRef.current;
    if (!wrapper) return;
    svg.setAttribute("width", wrapper.offsetWidth);
    svg.setAttribute("height", wrapper.offsetHeight);
    // Source/dest positions
    const srcs = sources.map((s) => ({ x: 90, y: s.top + 40 }));
    const dsts = destinations.map((dst, idx) => ({ x: wrapper.offsetWidth - 90, y: dst.top + 40, index: idx }));
    const center = { x: wrapper.offsetWidth / 2, y: wrapper.offsetHeight / 2 };
    const aiPos = { x: center.x, y: center.y - 180 }; // AI position above Confluent
    // Store path elements for animation
    const animatedPaths = [];
    // Map activeTab to destination index (0=Splunk, 1=Elastic, 2=Chronicle, 3=ArcSight)
    const selectedDestIndex = activeTab;
    
    // Path 3: AI-Enriched Flow
    // Step 1: Sources to Confluent Hub (always blue)
    srcs.forEach((source) => {
      animatedPaths.push(createPath(source.x, source.y, center.x - 100, center.y, "#3b82f6", "solid", 2, 0.8));
    });
    
    // Step 2: Confluent to AI (purple, bidirectional feel)
    animatedPaths.push(createPath(center.x - 20, center.y - 50, aiPos.x - 20, aiPos.y + 50, "#a855f7", "solid", 2.5, 0.9));
    
    // Step 3: AI back to Confluent (purple)
    animatedPaths.push(createPath(aiPos.x + 20, aiPos.y + 50, center.x + 20, center.y - 50, "#a855f7", "solid", 2.5, 0.9));
    
    // Step 4: Confluent to SIEM platforms (highlight selected)
    dsts.slice(0, dsts.length - 1).forEach((dest) => {
      const isSelected = dest.index === selectedDestIndex;
      const color = isSelected ? "#10b981" : "#3b82f6"; // Green for selected, blue for others
      const opacity = isSelected ? 1 : 0.2; // Full opacity for selected, dim for others
      const width = isSelected ? 3.5 : 2.5; // Thicker for selected
      animatedPaths.push(createPath(center.x + 100, center.y, dest.x, dest.y, color, "solid", width, opacity));
    });
    // Add animated "sending light"
    let animationFrame;
    let t = 0;
    function animate() {
      t += 0.012;
      animatedPaths.forEach(({ path, color, width, light }) => {
        // Remove previous light
        if (light.parentNode) light.parentNode.removeChild(light);
        // Animate a circle along the path
        const pathLength = path.getTotalLength();
        const pos = path.getPointAtLength((pathLength * ((t + Math.random() * 0.2) % 1)));
        light.setAttribute('cx', pos.x);
        light.setAttribute('cy', pos.y);
        svg.appendChild(light);
      });
      animationFrame = requestAnimationFrame(animate);
    }
    animate();
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
    // Helper to create path and animated light
    function createPath(x1, y1, x2, y2, color, style = "solid", width = 2, opacity = 1) {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const ctrl1x = x1 + dx * 0.3;
      const ctrl1y = y1 + dy * 0.1;
      const ctrl2x = x1 + dx * 0.7;
      const ctrl2y = y2 - dy * 0.1;
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      const d = `M ${x1} ${y1} C ${ctrl1x} ${ctrl1y}, ${ctrl2x} ${ctrl2y}, ${x2} ${y2}`;
      path.setAttribute("d", d);
      path.setAttribute("stroke", color);
      path.setAttribute("stroke-width", width);
      path.setAttribute("fill", "none");
      path.setAttribute("opacity", opacity);
      path.setAttribute("stroke-dasharray", style === "dashed" ? "10 5" : "0");
      path.style.filter = `drop-shadow(0 0 6px ${color})`;
      path.style.transition = "opacity 0.5s ease";
      svg.appendChild(path);
      // Animated light
      const light = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      light.setAttribute('r', Math.max(4, width * 1.7));
      light.setAttribute('fill', color);
      light.setAttribute('opacity', 0.85);
      light.style.filter = `drop-shadow(0 0 12px ${color})`;
      return { path, color, width, light };
    }
  }, [activeTab]);
  return (
    <div
      ref={diagramRef}
      style={{
        position: 'relative',
        width: '100%',
        height: 1000,
        maxHeight: 1000,
        background: 'rgba(10,34,64,0.92)',
        borderRadius: 18,
        boxShadow: '0 4px 24px 0 rgba(30,144,255,0.10)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg ref={svgRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
      
      {/* AI Processing Node (Above Confluent) */}
      <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -280px)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ 
          border: '3px solid #a855f7', 
          borderRadius: '50%', 
          width: 70, 
          height: 70, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          fontSize: 36,
          background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(139, 92, 246, 0.3) 100%)',
          boxShadow: '0 0 20px rgba(168, 85, 247, 0.6), 0 0 40px rgba(168, 85, 247, 0.3)',
        }}>
          <span>🤖</span>
        </div>
        <div style={{ color: '#a855f7', fontWeight: 700, fontSize: 16, marginTop: 10, textShadow: '0 0 10px rgba(168, 85, 247, 0.8)' }}>AI Enriched</div>
      </div>
      
      {/* Central Confluent Hub */}
      <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img src="https://cdn.ivaluetv.com/oem/Confluent.png" alt="Confluent Logo" style={{ width: 'auto', height: 60, objectFit: 'contain', filter: 'invert(1) brightness(2)' }} />
        <div style={{ color: '#e0e7ef', fontWeight: 700, fontSize: 18, marginTop: 8, textShadow: '0 1px 6px #000' }}>Confluent Hub</div>
      </div>
      {/* Log Sources (Left) */}
      {sources.map((src, i) => (
        <div key={src.label} style={{ position: 'absolute', left: 10, top: src.top, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
          <div style={{ border: '2px solid #60a5fa', color: '#60a5fa', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, background: '#0a2240', boxShadow: '0 2px 8px #1e3a5f' }}>
            <span>{src.icon}</span>
          </div>
          <div style={{ marginTop: 8, color: '#e0e7ef', fontWeight: 500, fontSize: 12, textAlign: 'center', textShadow: '0 1px 6px #000', width: '100%' }}>{src.label}</div>
        </div>
      ))}
      {/* Destinations (Right) */}
      {destinations.slice(0, destinations.length - 1).map((dst, i) => (
        <div key={dst.label} style={{ position: 'absolute', right: 10, top: dst.top, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80 }}>
          <div style={{ border: `2px solid ${dst.color}`, color: dst.color, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', boxShadow: `0 2px 8px ${dst.color}` }}>
            <img src={dst.logo} alt={dst.label + ' logo'} style={{ width: 36, height: 36, objectFit: 'contain', filter: `drop-shadow(0 0 8px ${dst.color})` }} />
          </div>
          <div style={{ marginTop: 8, color: '#e0e7ef', fontWeight: 500, fontSize: 12, textAlign: 'center', textShadow: '0 1px 6px #000', width: '100%' }}>{dst.label}</div>
        </div>
      ))}
    </div>
  );
}

function Path3() {

  const [activeTab, setActiveTab] = useState(1);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });
 
  return (
    <Box sx={{ minHeight: '100vh', color: 'white', py: 6, px: { xs: 1, sm: 4 }, position: 'relative' }}>
      {/* User name at top right */}
      {user && user.name && (
        <Typography sx={{ position: 'absolute', top: 24, right: 32, fontWeight: 700, fontSize: 18, color: '#e0f7fa', letterSpacing: 0.5 }}>
          Welcome, {user.name}
        </Typography>
      )}
      <Typography variant="h3" sx={{ mb: 3, fontWeight: 600, letterSpacing: -1, background: 'linear-gradient(90deg, #00b4a0 0%, #1e90ff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        Path 3 (AI-Enriched) Dashboard
      </Typography>
      {/* SIEM Platform Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: '#b0bec5',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              minHeight: 48,
              position: 'relative',
            },
            '& .MuiTab-root.Mui-selected': {
              color: '#60a5fa',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#60a5fa',
              height: 3,
            },
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Splunk</span>
                <Box sx={{
                  backgroundColor: '#fbbf24',
                  color: '#000',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Beta
                </Box>
              </Box>
            }
          />
          <Tab label="Elastic" />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Chronicle</span>
                <Box sx={{
                  backgroundColor: '#fbbf24',
                  color: '#000',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Beta
                </Box>
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>ArcSight</span>
                <Box sx={{
                  backgroundColor: '#fbbf24',
                  color: '#000',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Beta
                </Box>
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Section 1: Metrics + Architecture */}
      <Grid container spacing={3} alignItems="stretch" sx={{ mb: 4 }}>
        {/* Left: Metrics - Dynamic based on selected tab */}
        {activeTab === 0 && <Path3Splunk />}
        {activeTab === 1 && <Path3Elastic />}
        {activeTab === 2 && <Path3Chronicle />}
        {activeTab === 3 && <Path3ArcSight />}
        
        {/* Right: Architecture Path 3 */}
        <Grid item xs={12} md={7}>
          <ArchitecturePath3 activeTab={activeTab} />
        </Grid>
      </Grid>
      {/* Section 2: Event List (Card style like Live Alerts) */}
      <Card sx={{ background: 'rgba(10,34,64,0.92)', borderRadius: 3, boxShadow: 3, minHeight: 320, mt: 2, mb: 6 }}>
        {activeTab === 0 && <LiveEventPath3Splunk />}
        {activeTab === 1 && <LiveEventPath3Elastic />}
        {activeTab === 2 && <LiveEventPath3Chronicle />}
        {activeTab === 3 && <LiveEventPath3ArcSight />}
  </Card>
  <Box sx={{ height: { xs: 32, sm: 48, md: 64 } }} />
  </Box>
  );
}

export default Path3;
